import React, { useState, useEffect } from 'react';
import {
  Text,
  MultiSelect,
  Button,
  Paper,
  Notification,
  Loader,
  Space,
  Divider,
  Checkbox,
} from '@mantine/core';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { FuelTemplateNames } from '@captiva/shared-types';
import { PageHeader } from '../components/shared';

export interface CaptivaFuelConfigsProps {
  companyId: string;
}

const templateOptions = [
  { value: FuelTemplateNames.CAPTURED_REFUEL, label: "Refuel at roadside filling stations" },
  { value: FuelTemplateNames.CAPTURED_FUEL_TANK_ISSUE, label: "Refuel at your own onsite tanks" },
  { value: FuelTemplateNames.CAPTURED_BULK_FUEL_RECEIVED, label: "Receive bulk fuel deliveries" },
  { value: FuelTemplateNames.CAPTURED_FUEL_TANK_TRANSFER, label: "Transfer fuel from tank to tank" },
  { value: FuelTemplateNames.CAPTURED_TANK_LEVEL_READING, label: "Read tank dipstick levels" },
];

interface ValidationLabelConfig {
  label: string;
  dataLabels: {
    [field: string]: string;
  };
}

interface ValidationLabels {
  [template: string]: ValidationLabelConfig;
}

const validationOptionLabels: ValidationLabels = {
  [FuelTemplateNames.CAPTURED_FUEL_TANK_ISSUE]: {
    label: "Onsite refuels",
    dataLabels: {
      odoOrHourReading: "Odometer/Hour Reading",
      tankMeterReadingEnd: "Tank Meter Reading (End)",
      tankMeterReadingStart: "Tank Meter Reading (Start)",
    },
  },
  [FuelTemplateNames.CAPTURED_REFUEL]: {
    label: "Roadside refuels",
    dataLabels: {
      totalPrice: "Total Price",
      totalQuantity: "Total Quantity",
    },
  }
};

interface VerifiedDataConfig {
  [field: string]: boolean;
}

interface ValidationConfigTemplate {
  verifiedData: VerifiedDataConfig;
}

interface ValidationConfig {
  [template: string]: ValidationConfigTemplate;
}

const validationOptions: ValidationConfig = {
  [FuelTemplateNames.CAPTURED_FUEL_TANK_ISSUE]: {
    verifiedData: {
      odoOrHourReading: true,
      tankMeterReadingEnd: true,
      tankMeterReadingStart: true,
    },
  },
  [FuelTemplateNames.CAPTURED_REFUEL]: {
    verifiedData: {
      totalPrice: true,
      totalQuantity: true,
    },
  },
};

const inAppValidationOptionLabels: ValidationLabels = {
  [FuelTemplateNames.CAPTURED_FUEL_TANK_ISSUE]: {
    label: "Onsite refuels",
    dataLabels: {
      odoOrHour: "Enforce increasing ODO/Hour Reading",
    },
  },
  [FuelTemplateNames.CAPTURED_REFUEL]: {
    label: "Roadside refuels",
    dataLabels: {
      odoOrHour: "Enforce increasing ODO/Hour Reading",
    },
  },
};

interface InAppValidationConfig {
  [template: string]: {
    [field: string]: boolean;
  };
}

const inAppValidationOptions: InAppValidationConfig = {
  [FuelTemplateNames.CAPTURED_FUEL_TANK_ISSUE]: {
    odoOrHour: true,
  },
  [FuelTemplateNames.CAPTURED_REFUEL]: {
    odoOrHour: true,
  },
};

export function CaptivaFuelConfigs({ companyId }: CaptivaFuelConfigsProps) {
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);
  const [initialLoading, setInitialLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [validationConfig, setValidationConfig] = useState<ValidationConfig>(validationOptions);
  const [inAppValidationConfig, setInAppValidationConfig] = useState<InAppValidationConfig>(inAppValidationOptions);

  useEffect(() => {
    if (companyId) {
      setInitialLoading(true);
      const fetchTemplates = async () => {
        try {
          const companyRef = doc(db, "companies", companyId);
          const docSnap = await getDoc(companyRef);
          if (docSnap.exists()) {
            const companyData = docSnap.data();
            setSelectedTemplates(companyData.selectedTemplates?.captivaFuel || []);
            setValidationConfig(
              companyData.autoValidationConfig?.captivaFuel ||
              companyData.validationConfig ||
              validationOptions
            );
            setInAppValidationConfig(
              companyData.inAppValidationConfig?.captivaFuel ||
              inAppValidationOptions
            );
          } else {
            setError("Company data not found.");
          }
        } catch (err) {
          console.error("Error fetching templates:", err);
          setError("Failed to load existing templates.");
        } finally {
          setInitialLoading(false);
        }
      };
      fetchTemplates();
    }
  }, [companyId]);

  const handleValidationChange = (template: string, field: string, value: boolean) => {
    setValidationConfig((prevConfig) => ({
      ...prevConfig,
      [template]: {
        ...prevConfig[template],
        verifiedData: {
          ...prevConfig[template].verifiedData,
          [field]: value,
        },
      },
    }));
  };

  const handleInAppValidationChange = (template: string, field: string, value: boolean) => {
    setInAppValidationConfig((prevConfig) => ({
      ...prevConfig,
      [template]: {
        ...prevConfig[template],
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    if (!companyId) {
      setError("Error: Company ID is missing. Cannot save.");
      return;
    }
    setIsSaving(true);
    setMessage('');
    setError('');

    try {
      const companyRef = doc(db, "companies", companyId);
      await updateDoc(companyRef, {
        selectedTemplates: {
          captivaFuel: selectedTemplates,
        },
        autoValidationConfig: {
          captivaFuel: {
            ...validationConfig,
          },
        },
        inAppValidationConfig: {
          captivaFuel: {
            ...inAppValidationConfig,
          },
        },
      });
      setMessage("Configurations saved successfully!");
    } catch (err) {
      console.error("Error saving configurations:", err);
      setError("Error saving configurations. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (initialLoading) {
    return (
      <div style={{ textAlign: 'center', paddingTop: '2rem' }}>
        <Loader />
        <Text>Loading configurations...</Text>
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Fuel App Configurations"
        subtitle="Manage settings for the Captiva Fuel native mobile application."
        alert={
          (message || error) ? (
            <>
              {message && (
                <Notification
                  title="Success"
                  color="green"
                  onClose={() => setMessage('')}
                  style={{ marginBottom: error ? '0.5em' : 0 }}
                >
                  {message}
                </Notification>
              )}
              {error && (
                <Notification
                  title="Error"
                  color="red"
                  onClose={() => setError('')}
                >
                  {error}
                </Notification>
              )}
            </>
          ) : undefined
        }
      />

      <Paper shadow="xs" p="md" style={{ maxWidth: '800px' }}>
        <Text size="lg" style={{ marginBottom: '0.5em' }}>Fueling Templates</Text>
        <Text size="sm" color="dimmed" style={{ marginBottom: '1em' }}>
          Select which fueling templates are relevant for your company's operations in the mobile app.
        </Text>

        <MultiSelect
          label="Available Templates"
          placeholder="Select templates"
          data={templateOptions}
          value={selectedTemplates}
          onChange={setSelectedTemplates}
          clearable
          searchable
          nothingFound="No templates found..."
          style={{ marginBottom: '1.5em' }}
        />
        <Divider my="md" />
        <Text size="lg" style={{ marginBottom: '0.5em' }}>Validation Settings</Text>
        <Text size="sm" color="dimmed" style={{ marginBottom: '1em' }}>
          Select which data fields should be auto validated against photos.
        </Text>
        {Object.keys(validationConfig).map((template) => {
          const labels = validationOptionLabels[template];
          if (!labels) return null;
          return (
            <div key={template} style={{ marginBottom: '1em' }}>
              <Text size="sm" weight="bold">{labels.label}</Text>
              {Object.keys(validationConfig[template].verifiedData).map((field) => (
                <div key={field} style={{ margin: '0.5em' }}>
                  <Checkbox
                    label={labels.dataLabels[field] || field}
                    checked={validationConfig[template].verifiedData[field]}
                    color='cap-blue'
                    onChange={(e) =>
                      handleValidationChange(template, field, e.target.checked)
                    }
                  />
                </div>
              ))}
            </div>
          );
        })}

        <Divider my="md" />
        <Text size="lg" style={{ marginBottom: '0.5em' }}>In-App Capture Validation</Text>
        <Text size="sm" color="dimmed" style={{ marginBottom: '1em' }}>
          Configure blocking validation checks enforced during mobile form capture.
        </Text>
        {Object.keys(inAppValidationConfig).map((template) => {
          const labels = inAppValidationOptionLabels[template];
          return (
            <div key={template} style={{ marginBottom: '1em' }}>
              <Text size="sm" weight="bold">{labels?.label || template}</Text>
              {Object.keys(inAppValidationConfig[template]).map((field) => (
                <div key={field} style={{ margin: '0.5em' }}>
                  <Checkbox
                    label={labels?.dataLabels[field] || field}
                    checked={inAppValidationConfig[template][field]}
                    color='cap-blue'
                    onChange={(e) =>
                      handleInAppValidationChange(template, field, e.target.checked)
                    }
                  />
                </div>
              ))}
            </div>
          );
        })}

        <Button
          onClick={handleSave}
          loading={isSaving}
          disabled={isSaving || initialLoading}
          color="cap-blue"
        >
          Save Configurations
        </Button>
      </Paper>
    </>
  );
}

export default CaptivaFuelConfigs;
